import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Sparkles, Loader2, Target, Clock, CheckCircle, XCircle,
  Play, TrendingUp, AlertTriangle, Zap, BookOpen, Shield, Settings, RotateCcw,
  Download, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  process: Settings, technical: Zap, onboarding: BookOpen,
  compliance: Shield, strategy: Target, operations: TrendingUp,
};

const KnowledgePredictionsPage = () => {
  const { token } = useAuth();
  const [predictionSets, setPredictionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeSet, setActiveSet] = useState(null);

  useEffect(() => { fetchPredictions(); }, []);

  const fetchPredictions = async () => {
    try {
      const res = await axios.get(`${API}/knowledge-predictions`, { headers: { Authorization: `Bearer ${token}` } });
      setPredictionSets(res.data);
      if (res.data.length > 0) setActiveSet(res.data[0]);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const generatePredictions = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/knowledge-predictions/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setActiveSet(res.data);
      fetchPredictions();
      toast.success(`Generated ${res.data.predictions?.length || 0} predictions`);
    } catch { toast.error('Failed to generate predictions'); }
    finally { setGenerating(false); }
  };

  const handleAction = async (predictionId, action) => {
    try {
      await axios.post(`${API}/knowledge-predictions/${predictionId}/action`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Prediction ${action}ed`);
      fetchPredictions();
    } catch { toast.error('Failed'); }
  };

  const getUrgencyBadge = (u) => {
    switch (u) {
      case 'immediate': return 'bg-[#FF3B30] text-white';
      case 'short_term': return 'bg-[#FF9500] text-white';
      case 'medium_term': return 'bg-[#FFCC00] text-[#0A0A0A]';
      default: return 'bg-[#34C759] text-white';
    }
  };

  const getEffortBadge = (e) => {
    switch (e) {
      case 'large': return 'bg-[#AF52DE] text-white';
      case 'medium': return 'bg-[#007AFF] text-white';
      default: return 'bg-[#34C759] text-white';
    }
  };

  const getStatusIcon = (s) => {
    switch (s) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-[#34C759]" />;
      case 'in_progress': return <Play className="w-4 h-4 text-[#007AFF]" />;
      case 'dismiss': return <XCircle className="w-4 h-4 text-[#9CA3AF]" />;
      default: return <Clock className="w-4 h-4 text-[#FF9500]" />;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  return (
    <div data-testid="knowledge-predictions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Gap Predictions</h1>
          <p className="text-[#4B5563] text-sm">AI-powered predictions of upcoming knowledge needs</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
            try { const res = await axios.get(`${API}/knowledge-predictions/export-pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = 'gap-predictions-report.pdf'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { toast.error('Download failed'); }
          }} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const emails = prompt('Enter email addresses (comma-separated):'); if (!emails) return;
            const message = prompt('Add a personal message (optional):') || '';
            try { const res = await axios.post(`${API}/knowledge-predictions/share`, { to_emails: emails.split(',').map(e => e.trim()).filter(Boolean), message }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(res.data.message); } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); }
          }} data-testid="share-email-btn"><Mail className="w-3.5 h-3.5 mr-1" />Share</Button>
          <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={generatePredictions} disabled={generating} data-testid="generate-predictions-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Predictions</>}
          </Button>
        </div>
      </div>

      {!activeSet ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="text-center py-16">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#E5E7EB]" />
            <h2 className="text-lg font-semibold mb-2">No Predictions Yet</h2>
            <p className="text-[#9CA3AF] mb-4">Generate AI-powered predictions based on your knowledge dependency graph, audit data, and team activity.</p>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={generatePredictions} disabled={generating}>
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate First Predictions</>}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Context Summary */}
          {activeSet.context_summary && (
            <div className="flex gap-4 mb-6 text-sm text-[#4B5563] flex-wrap">
              <span><strong>{activeSet.context_summary.total_assets}</strong> assets analyzed</span>
              <span><strong>{activeSet.context_summary.team_size}</strong> team members</span>
              <span><strong>{activeSet.context_summary.graph_edges}</strong> dependencies</span>
              <span><strong>{activeSet.context_summary.isolated}</strong> isolated assets</span>
              {activeSet.context_summary.trending?.length > 0 && (
                <span>Trending: {activeSet.context_summary.trending.join(', ')}</span>
              )}
              <span className="text-[#9CA3AF]">Generated: {activeSet.created_at ? format(new Date(activeSet.created_at), 'MMM d, h:mm a') : 'N/A'}</span>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="prediction-stats">
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#002FA7]">{activeSet.predictions?.length || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Total Predictions</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#FF3B30]">{activeSet.predictions?.filter(p => p.urgency === 'immediate').length || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Immediate</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#34C759]">{activeSet.predictions?.filter(p => p.status === 'completed').length || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Completed</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{Math.round((activeSet.predictions || []).reduce((s, p) => s + (p.confidence || 0), 0) / Math.max(1, activeSet.predictions?.length || 1))}</p>
                <p className="text-xs text-[#9CA3AF]">Avg Confidence %</p>
              </CardContent>
            </Card>
          </div>

          {/* Predictions List */}
          <div className="space-y-3" data-testid="predictions-list">
            {(activeSet.predictions || []).map((p) => {
              const Icon = categoryIcons[p.category] || Target;
              return (
                <Card key={p.prediction_id} className={`border-[#E5E7EB] transition-colors ${p.status === 'dismiss' ? 'opacity-40' : p.status === 'completed' ? 'border-[#34C759]/30 bg-[#F0FFF4]' : 'hover:border-[#002FA7]/30'}`} data-testid={`prediction-${p.prediction_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Confidence circle */}
                      <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center" style={{ borderColor: p.confidence >= 80 ? '#34C759' : p.confidence >= 60 ? '#FF9500' : '#FFCC00' }}>
                        <span className="text-sm font-bold">{p.confidence}%</span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusIcon(p.status)}
                          <h3 className="font-semibold text-sm">{p.topic}</h3>
                        </div>
                        <p className="text-sm text-[#4B5563] mb-2">{p.reasoning}</p>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge className={getUrgencyBadge(p.urgency)}>{(p.urgency || '').replace('_', ' ')}</Badge>
                          <Badge variant="secondary" className="capitalize flex items-center gap-1"><Icon className="w-3 h-3" />{p.category}</Badge>
                          <Badge className={getEffortBadge(p.estimated_effort)}>{p.estimated_effort} effort</Badge>
                        </div>
                        <div className="bg-[#F3F4F6] rounded p-2 text-xs text-[#4B5563]">
                          <strong>Action:</strong> {p.suggested_action}
                        </div>
                      </div>

                      {/* Action buttons */}
                      {p.status === 'open' && (
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#34C759]" onClick={() => handleAction(p.prediction_id, 'accept')} data-testid={`accept-${p.prediction_id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" />Accept
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#007AFF]" onClick={() => handleAction(p.prediction_id, 'in_progress')} data-testid={`start-${p.prediction_id}`}>
                            <Play className="w-3 h-3 mr-1" />Start
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#9CA3AF]" onClick={() => handleAction(p.prediction_id, 'dismiss')}>
                            <XCircle className="w-3 h-3 mr-1" />Dismiss
                          </Button>
                        </div>
                      )}
                      {p.status === 'accept' && (
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#007AFF]" onClick={() => handleAction(p.prediction_id, 'in_progress')}>
                            <Play className="w-3 h-3 mr-1" />Start
                          </Button>
                        </div>
                      )}
                      {p.status === 'in_progress' && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-[#34C759]" onClick={() => handleAction(p.prediction_id, 'completed')}>
                          <CheckCircle className="w-3 h-3 mr-1" />Done
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* History */}
          {predictionSets.length > 1 && (
            <Card className="border-[#E5E7EB] mt-6">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" />Previous Predictions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {predictionSets.slice(1).map((ps) => (
                    <div key={ps.prediction_set_id} className="flex items-center justify-between p-2 rounded hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setActiveSet(ps)}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm">{format(new Date(ps.created_at), 'MMM d, yyyy h:mm a')}</span>
                        <Badge variant="secondary">{ps.predictions?.length || 0} predictions</Badge>
                      </div>
                      <Button variant="ghost" size="sm"><RotateCcw className="w-3 h-3 mr-1" />View</Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default KnowledgePredictionsPage;
